import { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Copy, RefreshCw, Key } from 'lucide-react';
import { toast } from 'sonner';

const JwtDecoderTool = () => {
  const [token, setToken] = useState('');
  const [decoded, setDecoded] = useState(null);
  const [error, setError] = useState('');

  const decodeJwt = (jwt) => {
    setError('');
    setDecoded(null);
    
    if (!jwt.trim()) return;

    try {
      const parts = jwt.split('.');
      if (parts.length !== 3) {
        setError('Invalid JWT format. Expected 3 parts separated by dots.');
        return;
      }

      const decodeBase64 = (str) => {
        const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
        const pad = base64.length % 4;
        const padded = pad ? base64 + '='.repeat(4 - pad) : base64;
        return JSON.parse(atob(padded));
      };

      const header = decodeBase64(parts[0]);
      const payload = decodeBase64(parts[1]);

      const now = Math.floor(Date.now() / 1000);
      let status = 'Valid';
      if (payload.exp && payload.exp < now) {
        status = 'Expired';
      } else if (payload.nbf && payload.nbf > now) {
        status = 'Not yet valid';
      }

      setDecoded({ header, payload, signature: parts[2], status });
    } catch (e) {
      setError('Failed to decode JWT: ' + e.message);
    }
  };

  const copySection = (section, name) => {
    navigator.clipboard.writeText(JSON.stringify(section, null, 2));
    toast.success(`${name} copied!`);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return null;
    return new Date(timestamp * 1000).toLocaleString();
  };

  return (
    <div className="space-y-6" data-testid="jwt-decoder-tool">
      <div>
        <label className="text-sm text-muted-foreground mb-2 block">JWT Token</label>
        <Textarea
          placeholder="Paste your JWT token here..."
          value={token}
          onChange={(e) => { setToken(e.target.value); decodeJwt(e.target.value); }}
          className="min-h-[100px] font-mono text-sm bg-muted/50"
        />
        {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
      </div>

      {decoded && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl bg-muted/30 border border-border">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Key className="w-4 h-4 text-primary" />
                Header
              </h3>
              <Button size="sm" variant="ghost" onClick={() => copySection(decoded.header, 'Header')}>
                <Copy className="w-3 h-3" />
              </Button>
            </div>
            <pre className="text-sm font-mono bg-background p-3 rounded overflow-auto">
              {JSON.stringify(decoded.header, null, 2)}
            </pre>
          </div>

          <div className="p-4 rounded-xl bg-muted/30 border border-border">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold">Payload</h3>
              <Button size="sm" variant="ghost" onClick={() => copySection(decoded.payload, 'Payload')}>
                <Copy className="w-3 h-3" />
              </Button>
            </div>
            <pre className="text-sm font-mono bg-background p-3 rounded overflow-auto max-h-[200px]">
              {JSON.stringify(decoded.payload, null, 2)}
            </pre>
          </div>

          <div className="md:col-span-2 p-4 rounded-xl bg-muted/30 border border-border">
            <h3 className="font-semibold mb-3">Token Info</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Status</span>
                <p className={`font-semibold ${decoded.status === 'Valid' ? 'text-emerald-400' : 'text-red-400'}`}>
                  {decoded.status}
                </p>
              </div>
              {decoded.payload.iat && (
                <div>
                  <span className="text-muted-foreground">Issued At</span>
                  <p className="font-mono text-xs">{formatDate(decoded.payload.iat)}</p>
                </div>
              )}
              {decoded.payload.exp && (
                <div>
                  <span className="text-muted-foreground">Expires</span>
                  <p className="font-mono text-xs">{formatDate(decoded.payload.exp)}</p>
                </div>
              )}
              {decoded.payload.sub && (
                <div>
                  <span className="text-muted-foreground">Subject</span>
                  <p className="font-mono text-xs truncate">{decoded.payload.sub}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button onClick={() => { setToken(''); setDecoded(null); setError(''); }} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Clear
        </Button>
      </div>
    </div>
  );
};

export default JwtDecoderTool;
